import _ from 'lodash'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Control } from '@/gen/inventory/v1/control_service_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'
import { buildSettingsSectionUrl } from '@/lib/url/urls'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'
import { CONTROLS_TAB_LABELS } from '@/pages/assessment/assessment-tabs/labels'

import { TagsList } from '@/components/lemmala-design-system/tag/tag'

import { OverviewCard } from './overview-card'

type ControlsCardProps = {
  controls: Control[]
  projectId?: string
  readonly?: boolean
}
export const ControlsCard = ({ controls, readonly = false, projectId }: ControlsCardProps) => {
  const trackingContext = useAssessmentAnalytics()
  const { updateParam } = useUrlParams()
  const navigate = useNavigate()
  const trackTabShortcutClick = useTrackCallback(
    'third-party.assessment.overview.tab-shortcut.click',
  )
  const hasProjects = !!projectId
  const isProjectWithoutControls = !readonly && hasProjects && controls.length === 0

  const scopeNamesCount = useMemo(() => {
    return _.countBy(controls, 'scope.name')
  }, [controls])
  const actionLabel = isProjectWithoutControls
    ? CONTROLS_TAB_LABELS.GO_TO_PROJECT_SETTINGS
    : CONTROLS_TAB_LABELS.VIEW_ALL_CONTROLS

  return (
    <OverviewCard
      variant={isProjectWithoutControls ? 'disabled' : 'default'}
      actionVariant='ghost'
      title='Controls'
      mainNumber={controls.length}
      details={
        isProjectWithoutControls
          ? ["This project doesn't have any control scopes assigned. "]
          : [
              <TagsList
                key={'controls-tags-list'}
                maxDisplay={6}
                tags={Object.entries(scopeNamesCount).map(([name, count]) => ({
                  id: name,
                  name: `${name} (${count})`,
                }))}
              />,
            ]
      }
      action={{
        label: actionLabel,
        onClick: () => {
          if (trackingContext) {
            trackTabShortcutClick({
              ...trackingContext,
              destination: 'controls',
              label: actionLabel,
            })
          }
          if (isProjectWithoutControls) {
            navigate(buildSettingsSectionUrl('projects', { [UrlParam.PROJECT_ID]: projectId }))
          } else {
            updateParam(
              readonly ? UrlParam.PAST_ASSESSMENT_TAB : UrlParam.ASSESSMENT_TAB,
              ASSESSMENT_TABS.CONTROLS,
            )
          }
        },
      }}
    />
  )
}
